/**
 * Synchronous capture of the MCP authorization interaction secret. It must be the first module
 * main.ts evaluates: the secret arrives in the URL fragment of `/mcp/authorize`, and neither
 * Sentry nor the router may ever see it. Like the attempt store it imports nothing from Angular
 * or the workspace, and it never throws, because it runs before the application can report
 * anything.
 */
import {
  isMcpInteractionSecret,
  isMcpRequestId,
  McpAttemptStore,
  mcpAttemptStore,
} from '../state/mcp-attempt.store'

// Mirror MCP_AUTH_LANDING_PATH of @ti/contracts, which this module cannot import; the spec pins it.
const LANDING_PATH = '/mcp/authorize'
const SIGN_IN_PATH = '/sign-in'

/** Tolerates doubled and trailing slashes, which the router resolves to the same route. */
const isPath = (pathname: string, path: string) =>
  pathname.replace(/\/{2,}/g, '/').replace(/\/+$/, '') === path

/** An internal return target that leads to the landing route (`/mcp/authorize`, not `/mcp/authorized`). */
export const isMcpLandingReturnUrl = (returnUrl: string) =>
  /^\/mcp\/authorize\/?(?:[?#]|$)/.test(returnUrl) && !returnUrl.includes('\\')

export type McpCaptureEnvironment = {
  location: Pick<Location, 'pathname' | 'search' | 'hash'>
  history: Pick<History, 'replaceState'>
  document?: Pick<Document, 'head' | 'querySelector' | 'createElement'> | null
  store: Pick<McpAttemptStore, 'put'>
}

export type McpCaptureResult = {
  /** The landing page itself, or an authentication page that returns to it. */
  isMcpAuthDocument: boolean
  /** A well-formed request id and interaction secret were stored. */
  captured: boolean
}

const readInteractionSecret = (hash: string): string | null => {
  const params = new URLSearchParams(hash.replace(/^#/, ''))
  let parameterCount = 0
  params.forEach(() => parameterCount++)
  const values = params.getAll('interaction')
  return parameterCount === 1 && values.length === 1 && isMcpInteractionSecret(values[0])
    ? values[0]
    : null
}

const readRequestId = (search: string): string | null => {
  const values = new URLSearchParams(search).getAll('request')
  return values.length === 1 && isMcpRequestId(values[0]) ? values[0] : null
}

const isAuthContinuationPath = (pathname: string) =>
  isPath(pathname, SIGN_IN_PATH) || /^\/(?:create|reset)-password\/[^/]+\/?$/.test(pathname)

export const isMcpAuthLocation = (location: Pick<Location, 'pathname' | 'search'>): boolean => {
  if (isPath(location.pathname, LANDING_PATH)) return true
  const returnUrls = new URLSearchParams(location.search).getAll('returnUrl')
  return (
    isAuthContinuationPath(location.pathname) &&
    returnUrls.length === 1 &&
    isMcpLandingReturnUrl(returnUrls[0])
  )
}

const markNoReferrer = (document: NonNullable<McpCaptureEnvironment['document']>) => {
  const existing = document.querySelector('meta[name="referrer"]')
  const meta = existing ?? document.createElement('meta')
  meta.setAttribute('name', 'referrer')
  meta.setAttribute('content', 'no-referrer')
  if (!existing) {
    document.head?.appendChild(meta)
  }
}

export function captureMcpInteraction(env: McpCaptureEnvironment): McpCaptureResult {
  const result: McpCaptureResult = { isMcpAuthDocument: false, captured: false }
  try {
    const { pathname, search, hash } = env.location
    const onLanding = isPath(pathname, LANDING_PATH)
    // Telemetry privacy is conservative even for an ambiguous return target. The profile policy
    // uses isMcpAuthLocation instead, which requires a single validated internal returnUrl.
    result.isMcpAuthDocument =
      onLanding ||
      (isAuthContinuationPath(pathname) &&
        new URLSearchParams(search).getAll('returnUrl').some(isMcpLandingReturnUrl))

    if (onLanding && hash) {
      // Read first, strip unconditionally: a malformed fragment must not stay in the address bar.
      const secret = readInteractionSecret(hash)
      const requestId = readRequestId(search)
      try {
        // The canonical path: a `//host`-style pathname would be read as a cross-origin URL.
        env.history.replaceState(null, '', LANDING_PATH + search)
      } catch {
        // Nothing sensible is left to do; the telemetry sanitizers still redact any fragment.
      }
      if (secret && requestId) {
        result.captured = env.store.put({ requestId, secret })
      }
    }

    if (result.isMcpAuthDocument && env.document) {
      markNoReferrer(env.document)
    }
  } catch {
    // The bootstrap must never prevent the application from starting.
  }
  return result
}

const captureCurrentDocument = (): McpCaptureResult =>
  typeof window === 'undefined'
    ? { isMcpAuthDocument: false, captured: false }
    : captureMcpInteraction({
        location: window.location,
        history: window.history,
        document: window.document,
        store: mcpAttemptStore,
      })

const capture = captureCurrentDocument()

/** Evaluated once, when the module is first imported. */
export const isMcpAuthDocument = capture.isMcpAuthDocument
